import React from "react";
import { Transaction } from "../../../services/types";
import { Pie, Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

interface TransactionAnalysisProps {
  transactions: Transaction[];
}

const TransactionAnalysis: React.FC<TransactionAnalysisProps> = ({
  transactions,
}) => {
  const totalTransactions = transactions.length;
  const totalAmount = transactions.reduce(
    (sum, transaction) => sum + transaction.amount,
    0
  );

  // Calculate average transaction amount
  const averageAmount =
    totalTransactions > 0 ? totalAmount / totalTransactions : 0;

  // Transaction Status Count (only successful and failed)
  const transactionStatusCount = transactions.reduce(
    (statusCount, transaction) => {
      // Only count successful and failed transactions
      if (
        transaction.status === "successful" ||
        transaction.status === "success"
      ) {
        statusCount.successful = (statusCount.successful || 0) + 1;
      } else {
        statusCount.failed = (statusCount.failed || 0) + 1;
      }
      return statusCount;
    },
    { successful: 0, failed: 0 } as Record<string, number>
  );

  // Transaction Type Count (by transaction type)
  const transactionTypeCount = transactions.reduce((typeCount, transaction) => {
    typeCount[transaction.type] = (typeCount[transaction.type] || 0) + 1;
    return typeCount;
  }, {} as Record<string, number>);

  // Amount Distribution Against Date (Last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentTransactions = transactions.filter(
    (transaction) => new Date(transaction.created_at) >= thirtyDaysAgo
  );

  const amountDistributionData = recentTransactions.reduce(
    (data, transaction) => {
      const date = new Date(transaction.created_at).toLocaleDateString(
        "en-US",
        {
          month: "short",
          day: "numeric",
        }
      );
      if (!data[date]) {
        data[date] = 0;
      }
      data[date] += transaction.amount;
      return data;
    },
    {} as Record<string, number>
  );
  // Successful vs Failed Transactions
  const successFailedCount = transactions.reduce(
    (count, transaction) => {
      if (transaction.status === "successful") {
        count.successful += 1;
      } else if (transaction.status === "failed") {
        count.failed += 1;
      } else {
        count.pending += 1;
      }
      return count;
    },
    { successful: 0, failed: 0, pending: 0 }
  );

  // Monthly trend data
  const monthlyData = transactions.reduce((data, transaction) => {
    const month = new Date(transaction.created_at).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    if (!data[month]) {
      data[month] = { count: 0, amount: 0 };
    }
    data[month].count += 1;
    data[month].amount += transaction.amount;
    return data;
  }, {} as Record<string, { count: number; amount: number }>);
  // Chart configurations
  const statusChartData = {
    labels: Object.keys(transactionStatusCount),
    datasets: [
      {
        data: Object.values(transactionStatusCount),
        backgroundColor: [
          "#FFAE0A", // Your amber for success
          "#EF4444", // Red for failed
          "#F59E0B", // Amber for pending
          "#3B82F6", // Blue for other
        ],
        borderColor: ["#FFAE0A", "#EF4444", "#F59E0B", "#3B82F6"],
        borderWidth: 2,
      },
    ],
  };

  const typeChartData = {
    labels: Object.keys(transactionTypeCount),
    datasets: [
      {
        data: Object.values(transactionTypeCount),
        backgroundColor: [
          "#8B5CF6", // Purple
          "#06B6D4", // Cyan
          "#F97316", // Orange
          "#EC4899", // Pink
          "#84CC16", // Lime
        ],
        borderColor: ["#8B5CF6", "#06B6D4", "#F97316", "#EC4899", "#84CC16"],
        borderWidth: 2,
      },
    ],
  };

  const lineChartData = {
    labels: Object.keys(amountDistributionData),
    datasets: [
      {
        label: "Transaction Amount (₦)",
        data: Object.values(amountDistributionData),
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#3B82F6",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  };

  const barChartData = {
    labels: ["Successful", "Failed", "Pending"],
    datasets: [
      {
        label: "Transaction Count",
        data: [
          successFailedCount.successful,
          successFailedCount.failed,
          successFailedCount.pending,
        ],
        backgroundColor: ["#FFAE0A", "#EF4444", "#F59E0B"],
        borderColor: ["#FFAE0A", "#EF4444", "#F59E0B"],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const monthlyChartData = {
    labels: Object.keys(monthlyData),
    datasets: [
      {
        label: "Transaction Count",
        data: Object.values(monthlyData).map((item) => item.count),
        backgroundColor: "rgba(139, 92, 246, 0.8)",
        borderColor: "#8B5CF6",
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: "Total Amount (₦)",
        data: Object.values(monthlyData).map((item) => item.amount),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "#3B82F6",
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: "bold" as const,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#3B82F6",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          callback: function (value: any) {
            return "₦" + value.toLocaleString();
          },
        },
      },
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Transaction Analytics
        </h2>
        <div className="text-sm text-gray-500">
          Total Records: {totalTransactions}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">
                Total Transactions
              </p>
              <p className="text-3xl font-bold">
                {totalTransactions.toLocaleString()}
              </p>
            </div>
            <div className="bg-blue-400 rounded-full p-3">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Amount</p>
              <p className="text-3xl font-bold">
                ₦{totalAmount.toLocaleString()}
              </p>
            </div>
            <div className="bg-green-400 rounded-full p-3">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">
                Average Amount
              </p>
              <p className="text-3xl font-bold">
                ₦{averageAmount.toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-400 rounded-full p-3">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">
                Success Rate
              </p>
              <p className="text-3xl font-bold">
                {totalTransactions > 0
                  ? (
                      (successFailedCount.successful / totalTransactions) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </p>
            </div>
            <div className="bg-orange-400 rounded-full p-3">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Transaction Status Distribution (Successful vs Failed)
          </h3>
          <div className="h-64">
            <Doughnut data={statusChartData} options={chartOptions} />
          </div>
        </div>

        {/* Transaction Types */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Transaction Types
          </h3>
          <div className="h-64">
            <Pie data={typeChartData} options={chartOptions} />
          </div>
        </div>

        {/* Amount Trend */}
        <div className="bg-gray-50 rounded-xl p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Amount Distribution (Last 30 Days)
          </h3>
          <div className="h-80">
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>

        {/* Success vs Failed */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Transaction Status
          </h3>
          <div className="h-64">
            <Bar data={barChartData} options={chartOptions} />
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Monthly Trends
          </h3>
          <div className="h-64">
            <Bar
              data={monthlyChartData}
              options={{
                ...chartOptions,
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: "rgba(0, 0, 0, 0.1)",
                    },
                  },
                  x: {
                    grid: {
                      color: "rgba(0, 0, 0, 0.1)",
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">
            Status Breakdown
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Successful</span>
              <span className="text-sm font-semibold text-amber-600">
                {successFailedCount.successful}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Failed</span>
              <span className="text-sm font-semibold text-red-600">
                {successFailedCount.failed}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pending</span>
              <span className="text-sm font-semibold text-yellow-600">
                {successFailedCount.pending}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">
            Top Transaction Types
          </h4>
          <div className="space-y-2">
            {Object.entries(transactionTypeCount)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 3)
              .map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">
                    {type}
                  </span>
                  <span className="text-sm font-semibold text-blue-600">
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">
            Performance Metrics
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span className="text-sm font-semibold text-amber-600">
                {totalTransactions > 0
                  ? (
                      (successFailedCount.successful / totalTransactions) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg. Amount</span>
              <span className="text-sm font-semibold text-purple-600">
                ₦{averageAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Volume</span>
              <span className="text-sm font-semibold text-blue-600">
                ₦{totalAmount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionAnalysis;
